import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, Play, Camera, FileText, Mic, ArrowRight, BookOpen, User, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/lib/api";

const IMAGES = [
  { file: "/media-kit/Chris-Merlo-Headshot.jpg", label: "Chris Merlo — Official Headshot", type: "Headshot" },
  { file: "/media-kit/Chris-Merlo-Speaking-On-Stage.jpg", label: "Chris Merlo — Speaking On Stage", type: "Event" },
  { file: "/media-kit/The-Forty-Year-Bender-Book-Stack.jpg", label: "The Forty Year Bender — Book Stack", type: "Book" },
  { file: "/media-kit/The-Forty-Year-Bender-Book-Cover.png", label: "The Forty Year Bender — Book Cover (Hi-Res)", type: "Cover" },
  { file: "/media-kit/The-Forty-Year-Bender-Audiobook-Artwork.png", label: "The Forty Year Bender — Audiobook Artwork", type: "Audiobook" },
];

const VIDEOS = [
  { file: "/media-kit/Chris-Merlo-Dopamine-Speaking-Reel.mp4", label: "Dopamine: The Chemical of Pursuit — Speaking Reel", desc: "Merls speaking on stage to a large audience about dopamine and nervous system spiking.", type: "file" },
  { youtube: "https://www.youtube.com/embed/Mhnloy3B7xs?si=BSAJDn6ErIISq30f", label: "The Forty Year Bender — 55 Second Introduction", desc: "A brief introduction to the book and Chris Merlo's CLARITY Protocol.", type: "youtube" },
];

function AccessGate({ onAccess }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) { toast.error("Please enter your name and email"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/media-access`, { name, email });
      sessionStorage.setItem("media_kit_email", email);
      sessionStorage.setItem("media_kit_name", name);
      onAccess(email);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] min-h-screen flex items-center justify-center px-6" data-testid="media-kit-gate">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <div className="w-14 h-14 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h1 className="font-heading text-3xl tracking-tighter uppercase text-white">Media Kit</h1>
          <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
            Access approved images, video reels, and press resources for Chris "Merls" Merlo and The Forty Year Bender.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
            data-testid="media-gate-name"
          />
          <Input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
            data-testid="media-gate-email"
          />
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
            data-testid="media-gate-submit"
          >
            {loading ? "Verifying..." : "Access Media Kit"}
          </Button>
        </form>

        <p className="text-zinc-600 text-xs text-center">For media and press use only. Your details will be shared with our media team.</p>

        <Link to="/" className="block text-center text-zinc-600 text-xs hover:text-zinc-400 transition-colors">
          <ArrowRight className="w-3 h-3 inline rotate-180 mr-1" /> Back to site
        </Link>
      </div>
    </div>
  );
}

export default function MediaKitPage() {
  const [form, setForm] = useState({ name: "", email: "", outlet: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Download capture state
  const [capturedEmail, setCapturedEmail] = useState(sessionStorage.getItem("media_kit_email") || "");
  const [capturedName, setCapturedName] = useState(sessionStorage.getItem("media_kit_name") || "");
  const [showCapture, setShowCapture] = useState(false);
  const [captureName, setCaptureName] = useState("");
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureLoading, setCaptureLoading] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  const trackDownload = async (asset, email) => {
    try {
      await axios.post(`${API}/media-download`, { email: email || capturedEmail, asset });
    } catch (err) {
      console.error("Download tracking failed:", err);
    }
  };

  const executeDownload = (file, label) => {
    trackDownload(label, capturedEmail);
    const a = document.createElement("a");
    a.href = file;
    a.download = file.split("/").pop();
    a.click();
  };

  const handleDownload = (file, label) => {
    if (capturedEmail) {
      executeDownload(file, label);
    } else {
      setPendingDownload({ file, label });
      setShowCapture(true);
    }
  };

  const handleCaptureSubmit = async () => {
    if (!captureName || !captureEmail) { toast.error("Please enter your name and email"); return; }
    setCaptureLoading(true);
    try {
      await axios.post(`${API}/media-access`, { name: captureName, email: captureEmail });
      sessionStorage.setItem("media_kit_email", captureEmail);
      sessionStorage.setItem("media_kit_name", captureName);
      setCapturedEmail(captureEmail);
      setCapturedName(captureName);
      setShowCapture(false);
      if (pendingDownload) {
        trackDownload(pendingDownload.label, captureEmail);
        const a = document.createElement("a");
        a.href = pendingDownload.file;
        a.download = pendingDownload.file.split("/").pop();
        a.click();
        setPendingDownload(null);
      }
      toast.success("Download starting...");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCaptureLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        message: `MEDIA KIT ENQUIRY\nOutlet: ${form.outlet}\n\n${form.message}`,
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Page is immediately accessible — no gate

  return (
    <div className="bg-[#050505] min-h-screen font-body" data-testid="media-kit-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
          <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">Media Kit</span>
        </div>
      </nav>

      {/* Hero */}
      <header className="pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4">Press Resources</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase text-white mb-6">
            Media Kit
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed max-w-2xl">
            Everything you need to cover the story of Chris "Merls" Merlo, The Forty Year Bender, and the CLARITY Protocol. Approved images, video reels, bio, and key facts — all in one place.
          </p>
          <div className="gold-line-left w-24 mt-8" />
        </div>
      </header>

      {/* Author Bio */}
      <section className="py-16 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <User className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">About Chris "Merls" Merlo</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-4">
              <p className="text-white font-semibold text-base leading-relaxed">
                Chris "Merls" Merlo is an Australian author, public speaker, and creator of the CLARITY Protocol — a 30-day nervous system reset framework born from forty years of lived experience with addiction and recovery.
              </p>
              <p className="text-zinc-400 text-base leading-relaxed">
                After seven rehabs, two accidental overdoses, arrests across two continents, and decades of what he calls "creative self-annihilation," Merls rebuilt himself from the ground up. His debut memoir, The Forty Year Bender, is a raw, unfiltered account of that journey — from rock bottom to redemption.
              </p>
              <p className="text-zinc-400 text-base leading-relaxed">
                Now a sought-after keynote speaker and recovery advocate, Merls brings his story to stages, podcasts, and media outlets across Australia. His approach is direct, irreverent, and deeply human — combining neuroscience, lived experience, and faith. He works one-on-one with individuals through his Recovery Protocol and speaks to audiences on topics including addiction, dopamine, nervous system regulation, burnout, and resilience.
              </p>
              <p className="text-zinc-400 text-base leading-relaxed">
                Based in Brisbane, Australia.
              </p>
            </div>
            <div>
              <img src="/media-kit/Chris-Merlo-Headshot.jpg" alt="Chris Merlo Headshot" className="w-full rounded-sm border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Facts */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Key Facts</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Full Name", value: "Chris 'Merls' Merlo" },
              { label: "Location", value: "Brisbane, QLD, Australia" },
              { label: "Book", value: "The Forty Year Bender — When Life Goes Off Track, There's a Way Back" },
              { label: "Published", value: "June 4, 2026" },
              { label: "Framework", value: "The CLARITY Protocol — 30-Day Nervous System Reset" },
              { label: "Speaking Topics", value: "Addiction & Recovery, Dopamine & Nervous System, Burnout & Resilience, Faith & Surrender" },
              { label: "Available For", value: "Keynote Speaking, Podcasts, Radio, TV, Print & Online Features" },
              { label: "Media Contact", value: "Sarah D'Ardenne — media@fybgroup.com.au" },
            ].map((fact) => (
              <div key={fact.label} className="bg-[#121212] border border-white/10 p-5 rounded-sm">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-1">{fact.label}</p>
                <p className="text-zinc-300 text-sm">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approved Images */}
      <section className="py-16 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Camera className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Approved Images</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-8">High-resolution images approved for editorial and media use. Click to download.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {IMAGES.map((img) => (
              <button
                key={img.file}
                onClick={() => handleDownload(img.file, img.label)}
                className="group block border border-white/10 rounded-sm overflow-hidden hover:border-[#D4AF37]/50 transition-colors text-left"
                data-testid={`download-${img.type.toLowerCase()}`}
              >
                <div className="aspect-square overflow-hidden bg-[#121212]">
                  <img src={img.file} alt={img.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{img.label}</p>
                    <p className="text-[10px] tracking-wider uppercase text-[#D4AF37] mt-1">{img.type}</p>
                  </div>
                  <Download className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition-colors" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Video Reels */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Play className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Video Reels</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-8">Sample footage of Chris Merlo speaking and introducing The Forty Year Bender.</p>
          <div className="space-y-8">
            {VIDEOS.map((vid) => (
              <div key={vid.label} className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                {vid.type === "youtube" ? (
                  <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={vid.youtube}
                      title={vid.label}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <video controls preload="metadata" className="w-full max-h-[500px] bg-black">
                    <source src={vid.file} type="video/mp4" />
                  </video>
                )}
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading text-lg tracking-tight uppercase text-white">{vid.label}</h3>
                    <p className="text-zinc-500 text-sm mt-1">{vid.desc}</p>
                  </div>
                  {vid.file && (
                    <button
                      onClick={() => handleDownload(vid.file, vid.label)}
                      className="flex-shrink-0 ml-4 border border-white/20 text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2 rounded-sm text-xs tracking-wider uppercase transition-colors flex items-center gap-2"
                    >
                      <Download className="w-3 h-3" /> Download
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Synopsis */}
      <section className="py-16 px-6 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Book Synopsis</h2>
          </div>
          <div className="bg-[#121212] border border-white/10 p-8 rounded-sm space-y-4">
            <h3 className="font-heading text-xl tracking-tight uppercase text-white">The Forty Year Bender</h3>
            <p className="text-[#D4AF37] italic text-base">When Life Goes Off Track, There's a Way Back</p>
            <Separator className="bg-white/10" />
            <p className="text-zinc-300 text-base leading-relaxed">
              Seven rehabs. Two accidental overdoses. Arrests on two continents. A broken marriage, financial ruin, and decades of creative self-annihilation fuelled by alcohol, pills, powder, and smoke.
            </p>
            <p className="text-zinc-300 text-base leading-relaxed">
              The Forty Year Bender is Chris Merlo's raw, unfiltered memoir of a life spent chasing dopamine spikes and the slow, messy, deeply human climb back to daylight. It's a story that will make you laugh, wince, shake your head, and maybe recognise yourself in his mess.
            </p>
            <p className="text-zinc-300 text-base leading-relaxed">
              Woven through the wild stories is the CLARITY Protocol — a seven-pillar framework Merlo developed for resetting the nervous system, born not in a laboratory but in the trenches of real life. Part memoir, part survival guide, part confession — this is the book for anyone who's ever hit bottom and wondered if there's a way back.
            </p>
            <p className="text-white font-semibold text-base">
              There is. And this is how.
            </p>
          </div>
        </div>
      </section>

      {/* Downloadable Documents */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Press Documents</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => handleDownload("/media-kit/The-Forty-Year-Bender-Press-Release.pdf", "Press Release PDF")}
              className="group bg-[#121212] border border-white/10 hover:border-[#D4AF37]/50 p-6 rounded-sm flex items-center gap-4 transition-colors text-left"
              data-testid="download-press-release"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-grow">
                <p className="text-white text-sm font-medium">Press Release</p>
                <p className="text-zinc-500 text-xs mt-1">Official press release — PDF download</p>
              </div>
              <Download className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
            </button>
            <button
              onClick={() => handleDownload("/downloads/TheFortyYearBender_Prologue.pdf", "Book Prologue PDF")}
              className="group bg-[#121212] border border-white/10 hover:border-[#D4AF37]/50 p-6 rounded-sm flex items-center gap-4 transition-colors text-left"
              data-testid="download-prologue"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-grow">
                <p className="text-white text-sm font-medium">Book Prologue</p>
                <p className="text-zinc-500 text-xs mt-1">Sample chapter — PDF download</p>
              </div>
              <Download className="w-4 h-4 text-zinc-600 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
            </button>
          </div>
        </div>
      </section>

      {/* Media Enquiry Form */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Mic className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-heading text-2xl tracking-tight uppercase text-white">Media & Podcast Enquiries</h2>
            </div>
            <p className="text-zinc-400 text-sm">
              For interview requests, podcast appearances, and feature opportunities.
            </p>
            <p className="text-zinc-500 text-xs mt-2">Contact: media@fybgroup.com.au</p>
          </div>

          <div className="bg-[#121212] border border-[#D4AF37]/30 p-8 rounded-sm">
            {!submitted ? (
              <div className="space-y-4">
                <Input
                  type="text"
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="media-kit-name"
                />
                <Input
                  type="email"
                  placeholder="Your email *"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="media-kit-email"
                />
                <Input
                  type="text"
                  placeholder="Publication / Show / Podcast name"
                  value={form.outlet}
                  onChange={(e) => setForm({ ...form, outlet: e.target.value })}
                  className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
                  data-testid="media-kit-outlet"
                />
                <textarea
                  placeholder="Tell us about the opportunity... *"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={4}
                  className="w-full bg-black border border-white/20 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none text-white placeholder:text-zinc-600 px-3 py-3 rounded-sm text-sm"
                  data-testid="media-kit-message"
                />
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
                  data-testid="media-kit-submit"
                >
                  {loading ? "Sending..." : "Submit Enquiry"}
                </Button>
              </div>
            ) : (
              <div className="text-center py-8 space-y-4" data-testid="media-kit-success">
                <div className="w-16 h-16 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center mx-auto">
                  <Mic className="w-7 h-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-heading text-2xl tracking-tight uppercase text-white">Thank You</h3>
                <p className="text-zinc-400 text-sm">Your enquiry has been received. Our media team will be in touch within 48 hours.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050505] border-t border-white/10 py-12 px-6 text-center">
        <div className="max-w-4xl mx-auto space-y-4">
          <p className="text-zinc-500 text-sm">All images and videos on this page are approved for editorial and media use in connection with coverage of Chris Merlo, The Forty Year Bender, and FYB Life.</p>
          <p className="font-heading text-xs tracking-[0.16em] uppercase text-zinc-600">
            FYB &middot; Find Your Breakthrough &middot; fyblife.com.au
          </p>
        </div>
      </footer>

      {/* Download Capture Modal */}
      <Dialog open={showCapture} onOpenChange={setShowCapture}>
        <DialogContent className="bg-[#121212] border border-[#D4AF37]/30 max-w-md" data-testid="download-capture-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight uppercase text-white">Download Asset</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Enter your details to download. Your information will be shared with our media team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              type="text"
              placeholder="Your name"
              value={captureName}
              onChange={(e) => setCaptureName(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="capture-name-input"
            />
            <Input
              type="email"
              placeholder="Your email"
              value={captureEmail}
              onChange={(e) => setCaptureEmail(e.target.value)}
              className="bg-black border-white/20 focus:border-[#D4AF37] focus:ring-[#D4AF37] text-white placeholder:text-zinc-600 h-12"
              data-testid="capture-email-input"
            />
            <Button
              onClick={handleCaptureSubmit}
              disabled={captureLoading}
              className="w-full btn-gold h-12 rounded-sm text-xs tracking-[0.15em] uppercase"
              data-testid="capture-submit-button"
            >
              {captureLoading ? "Processing..." : "Download"}
            </Button>
            <p className="text-zinc-600 text-[10px] text-center">For media and press use only.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
